import { inject } from '@angular/core';
import { CanDeactivateFn, PRIMARY_OUTLET, Router } from '@angular/router';
import { JudgeReviewStudioViewModelService } from '../shared/services/judge-review-studio-view-model.service';
import { JrCheckAddressCompletionService } from './services/jr-check-address-completion.service';

/**
 * The judge-review route segment, held locally rather than read from `ROUTES` in
 * `@routes`.
 *
 * `routes.ts` imports this guard eagerly to wire `canDeactivate`, so importing
 * `@routes` back from here forms a cycle. Depending on which module the bundler
 * enters first, the guard's own `const` is still in its temporal dead zone when
 * `routes.ts` reads it — which throws
 * `Cannot access 'jrVerifyUnrepresentedGuard' before initialization` and takes
 * the whole bundle down. Must stay in sync with `ROUTES.JUDGE_REVIEW`.
 */
const JUDGE_REVIEW_SEGMENT = 'judge-review';

/** The address-collection child segment under `judge-review/:filingUniqueId`. */
const ADDRESS_SEGMENT = 'address';

/** The confirm child segment under `judge-review/:filingUniqueId`. */
const CONFIRM_SEGMENT = 'confirm';

/**
 * Splits a serialized URL into its primary-outlet path segments.
 *
 * Parsed rather than string-matched: substring tests against the serialized URL
 * also match secondary outlets and any future route that merely contains the same
 * fragment (`sidepanel-check-address`, an `address-book` route), which would
 * silently disable the guard.
 *
 * @param router - The router, used for its URL serializer.
 * @param url - The serialized URL to parse.
 * @returns The primary-outlet segment paths, root first.
 */
function primarySegments(router: Router, url: string): string[] {
  const segments: string[] = [];
  let group = router.parseUrl(url).root;

  while (group) {
    segments.push(...group.segments.map(segment => segment.path));
    group = group.children[PRIMARY_OUTLET];
  }

  return segments;
}

/**
 * CanDeactivate guard for the JR recipients page.
 *
 * Intercepts navigation away when one or more selected unrepresented
 * participants have been chosen, redirecting to the address-collection page
 * ({@link JrCheckAddressComponent}) before allowing the user to continue to
 * the confirmation step.
 *
 * Mirrors the subF {@link verifyProSePartyGuard} canDeactivate pattern.
 *
 * The guard is bypassed when:
 * - {@link JrCheckAddressCompletionService.isComplete} is already true (the
 *   judge completed address collection this session).
 * - The navigation is not to this filing's confirm page (Back to Review & Sign
 *   or the transaction, the address page itself, or leaving the JR flow).
 * - No unrepresented participants are selected.
 */
export const jrVerifyUnrepresentedGuard: CanDeactivateFn<unknown> = (
  _component,
  currentRoute,
  currentState,
  nextState,
) => {
  const router = inject(Router);
  const studioVm = inject(JudgeReviewStudioViewModelService);
  const completion = inject(JrCheckAddressCompletionService);

  // Already done this session — let navigation through.
  if (completion.isComplete()) return true;

  const filingUniqueId: string = currentRoute.params['filingUniqueId'] ?? '';
  if (!filingUniqueId) return true;

  const nextSegments = primarySegments(router, nextState.url);

  // Address collection belongs to the forward path only. Back (Review & Sign,
  // transaction) and the address page itself pass through.
  const isConfirmPage =
    nextSegments[0] === JUDGE_REVIEW_SEGMENT &&
    nextSegments[1] === filingUniqueId &&
    nextSegments[2] === CONFIRM_SEGMENT;
  if (!isConfirmPage) return true;

  // Check for selected unrepresented participants.
  const unrepresented = studioVm.selectedUnrepresentedParticipants();
  if (unrepresented.length === 0) return true;

  // Redirect to address collection, preserving where the user was headed.
  return router.createUrlTree(
    ['/', JUDGE_REVIEW_SEGMENT, filingUniqueId, ADDRESS_SEGMENT],
    {
      queryParams: {
        dest: nextState.url,
        origin: currentState.url,
      },
    },
  );
};
